<template>
  <div v-if="links.length > 3" class="pagination-container">
    <ul class="pagination" role="navigation" aria-label="Pagination Navigation">
      <!-- Previous Page Button -->
      <li class="page-item">
        <Link 
          v-if="hasPrevPage"
          :href="prevPageUrl" 
          class="page-link prev-link"
          aria-label="Previous page"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M15 18l-6-6 6-6"/>
          </svg>
        </Link>
        <span 
          v-else
          class="page-link prev-link disabled"
          aria-disabled="true"
          aria-label="Previous page"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M15 18l-6-6 6-6"/>
          </svg>
        </span>
      </li>

      <!-- First Page Button -->
      <li v-if="currentPage > 3" class="page-item">
        <Link :href="links[1].url" class="page-link" aria-label="Go to first page">1</Link>
      </li>

      <!-- Ellipsis for left side -->
      <li v-if="currentPage > 4" class="page-item ellipsis">
        <span class="page-link" aria-hidden="true">...</span>
      </li>

      <!-- Numbered Pages -->
      <li v-for="page in visiblePages" :key="page.label" class="page-item">
        <Link 
          v-if="!page.ellipsis" 
          :href="page.url" 
          :class="['page-link', { active: page.active }]"
          :aria-current="page.active ? 'page' : null"
        >
          {{ page.label }}
        </Link>
        <span v-else class="page-link ellipsis" aria-hidden="true">...</span>
      </li>

      <!-- Ellipsis for right side -->
      <li v-if="currentPage < lastPage - 3" class="page-item ellipsis">
        <span class="page-link" aria-hidden="true">...</span>
      </li>

      <!-- Last Page Button -->
      <li v-if="currentPage < lastPage - 2" class="page-item">
        <Link :href="links[links.length - 2].url" class="page-link" aria-label="Go to last page">{{ lastPage }}</Link>
      </li>

      <!-- Next Page Button -->
      <li class="page-item">
        <Link 
          v-if="hasNextPage"
          :href="nextPageUrl" 
          class="page-link next-link"
          aria-label="Next page"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M9 18l6-6-6-6"/>
          </svg>
        </Link>
        <span 
          v-else
          class="page-link next-link disabled"
          aria-disabled="true"
          aria-label="Next page"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M9 18l6-6-6-6"/>
          </svg>
        </span>
      </li>
    </ul>
    
    <!-- Page Info -->
    <div class="pagination-info" v-if="showPageInfo">
      Page {{ currentPage }} of {{ lastPage }}
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { Link } from '@inertiajs/vue3';

const props = defineProps({
  links: {
    type: Array,
    required: true,
  },
  showPageInfo: {
    type: Boolean,
    default: true,
  },
  maxVisiblePages: {
    type: Number,
    default: 5
  }
});

// Find the current page
const currentPage = computed(() => {
  const currentLink = props.links.find(link => link.active);
  return currentLink ? parseInt(currentLink.label) : 1;
});

// Calculate the last page number
const lastPage = computed(() => {
  const lastLink = props.links[props.links.length - 2];
  return lastLink ? parseInt(lastLink.label) : 1;
});

// Determine if there's a previous page
const hasPrevPage = computed(() => currentPage.value > 1);

// Determine if there's a next page
const hasNextPage = computed(() => currentPage.value < lastPage.value);

// Get the previous page URL
const prevPageUrl = computed(() => {
  return hasPrevPage.value 
    ? props.links.find(link => parseInt(link.label) === currentPage.value - 1)?.url
    : null;
});

// Get the next page URL
const nextPageUrl = computed(() => {
  return hasNextPage.value 
    ? props.links.find(link => parseInt(link.label) === currentPage.value + 1)?.url
    : null;
});

// Calculate which page numbers to show
const visiblePages = computed(() => {
  if (lastPage.value <= props.maxVisiblePages) {
    // If total pages are less than or equal to maxVisiblePages, show all
    return props.links.slice(1, -1);
  }

  // Calculate the range around the current page
  const halfVisible = Math.floor(props.maxVisiblePages / 2);
  let start = Math.max(1, currentPage.value - halfVisible);
  let end = Math.min(lastPage.value, start + props.maxVisiblePages - 1);

  // Adjust start if end is at max
  if (end === lastPage.value) {
    start = Math.max(1, end - props.maxVisiblePages + 1);
  }

  // Create the array of visible page links
  return props.links
    .slice(1, -1) // Remove prev and next links
    .filter(link => {
      const pageNum = parseInt(link.label);
      return pageNum >= start && pageNum <= end;
    });
});
</script>

<style scoped>
.pagination-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  margin: 2rem 0;
}

.pagination {
  display: flex;
  list-style-type: none;
  padding: 0;
  gap: 0.25rem;
  margin: 0;
  flex-wrap: wrap;
  justify-content: center;
}

.page-item {
  margin: 0.125rem;
}

.page-link {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 2.5rem;
  height: 2.5rem;
  padding: 0 0.5rem;
  background-color: rgba(55, 65, 81, 0.6); /* gray-700 with transparency */
  color: #e5e7eb; /* gray-200 */
  border-radius: 0.5rem;
  font-weight: 500;
  transition: all 0.2s ease;
  text-decoration: none;
  box-shadow: none;
  user-select: none;
  backdrop-filter: blur(4px);
}

.page-link svg {
  width: 1.25rem;
  height: 1.25rem;
}

.page-link:hover:not(.disabled):not(.active) {
  background-color: rgba(75, 85, 99, 0.7); /* gray-600 with transparency */
  transform: translateY(-2px);
  box-shadow: none;
}

.page-link.active {
  background: linear-gradient(90deg, rgba(59, 130, 246, 0.8), rgba(139, 92, 246, 0.8)); /* blue-500 to purple-500 with transparency */
  color: white;
  font-weight: 600;
  box-shadow: none;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.prev-link, .next-link {
  background-color: rgba(31, 41, 55, 0.7); /* gray-800 with transparency */
}

.ellipsis {
  color: #9ca3af; /* gray-400 */
}

.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

.pagination-info {
  font-size: 0.875rem;
  color: #9ca3af; /* gray-400 */
}

/* Responsive styles */
@media (max-width: 640px) {
  .page-link {
    min-width: 2rem;
    height: 2rem;
    font-size: 0.875rem;
  }
  
  .pagination {
    gap: 0.125rem;
  }
  
  .page-item.ellipsis {
    display: none;
  }
}

/* Animation for active hover */
@keyframes pulse {
  0%, 100% {
    background: linear-gradient(90deg, rgba(59, 130, 246, 0.8), rgba(139, 92, 246, 0.8));
  }
  50% {
    background: linear-gradient(90deg, rgba(79, 150, 255, 0.9), rgba(159, 112, 255, 0.9));
  }
}

.page-link.active:hover {
  animation: pulse 1.5s infinite;
}
</style>